import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { getAuthData, getStoreId } from "../utils/auth";

const THUMB_PX = 56;

export default function MainImagePicker() {
  const storeId = getStoreId();
  const apiUrl = import.meta.env.VITE_BACKEND_URL;

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [listingScope, setListingScope] = useState("online");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [flash, setFlash] = useState(null);

  const fetchInFlight = useRef(false);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const fetchProducts = useCallback(async () => {
    if (!storeId) return;
    if (fetchInFlight.current) return;
    fetchInFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        id: storeId,
        page: String(page),
        limit: String(limit),
      });
      if (search.trim()) params.set("search", search.trim());
      if (listingScope === "online") params.set("onlineOnly", "true");
      const auth = getAuthData();
      if (auth?.access_token) params.set("token", auth.access_token);

      const res = await fetch(`${apiUrl}/api/main-image-picker/products?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.error || "Could not load products.";
        setError(msg);
        setFlash({ type: "danger", text: msg });
        setProducts([]);
        setTotal(0);
        return;
      }
      setProducts(data.products || []);
      setTotal(typeof data.total === "number" ? data.total : 0);
    } catch (e) {
      console.error("MainImagePicker fetch failed", e);
      setError("Could not load products.");
      setFlash({ type: "danger", text: "Could not load products." });
    } finally {
      setLoading(false);
      fetchInFlight.current = false;
    }
  }, [storeId, apiUrl, page, limit, search, listingScope]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSetMain = async (productId, imageId) => {
    if (!storeId || busyKey) return;
    const key = `${productId}_${imageId}`;
    setBusyKey(key);
    try {
      const params = new URLSearchParams({ id: storeId });
      const auth = getAuthData();
      if (auth?.access_token) params.set("token", auth.access_token);

      const res = await fetch(`${apiUrl}/api/main-image-picker/set-main?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId, imageId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFlash({ type: "danger", text: data?.error || "Could not set primary image." });
        return;
      }
      setFlash({ type: "success", text: "Primary image updated." });
      await fetchProducts();
    } catch (e) {
      console.error("MainImagePicker set main failed", e);
      setFlash({ type: "danger", text: "Could not set primary image." });
    } finally {
      setBusyKey(null);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);
  const hasMultiplePages = totalPages > 1;

  return (
    <div className="container-fluid py-2 px-2 px-md-3">
      {flash && (
        <div className={`alert alert-${flash.type} py-2 px-2 small mb-2`} role="alert">
          {flash.text}
        </div>
      )}
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
        <div className="me-2" style={{ minWidth: 0 }}>
          <h1 className="h6 text-body mb-0 text-truncate">Pick primary storefront image</h1>
          <p className="text-muted mb-0" style={{ fontSize: "0.75rem", lineHeight: 1.35 }}>
            Click a thumbnail to set it as the main image shown in your storefront.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm py-0 px-2 d-inline-flex align-items-center gap-1 flex-shrink-0"
          disabled={loading}
          onClick={() => fetchProducts()}
        >
          <RefreshCw size={12} aria-hidden />
          Refresh
        </button>
      </div>

      <form className="d-flex flex-wrap align-items-end gap-2 mb-2" onSubmit={onSearchSubmit}>
        <div className="d-flex flex-wrap align-items-end gap-2 flex-grow-1" style={{ minWidth: 0 }}>
          <input
            type="search"
            className="form-control form-control-sm flex-grow-1"
            style={{ minWidth: "8rem", maxWidth: "22rem" }}
            placeholder="Search products…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search products"
          />
          <button type="submit" className="btn btn-primary btn-sm py-0">
            Search
          </button>
        </div>
        <div className="d-flex align-items-center gap-1 flex-shrink-0">
          <label htmlFor="main-image-picker-catalog" className="small text-muted mb-0 text-nowrap">
            Catalog
          </label>
          <select
            id="main-image-picker-catalog"
            className="form-select form-select-sm"
            style={{ width: "auto", minWidth: "9.5rem" }}
            value={listingScope}
            aria-label="Catalog filter"
            onChange={(e) => {
              setListingScope(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All products</option>
            <option value="online">Online only</option>
          </select>
        </div>
      </form>

      {error && !loading && (
        <div className="alert alert-warning py-2 px-2 small mb-2" role="alert">
          {error}
        </div>
      )}

      {loading && products.length === 0 ? (
        <div className="text-center py-4 text-muted small">Loading…</div>
      ) : (
        <div
          className="border rounded bg-white"
          style={{ fontSize: "0.8125rem" }}
        >
          {products.map((p, idx) => (
            <div
              key={p.id}
              className={`d-flex flex-wrap align-items-center gap-2 px-2 py-1 ${idx > 0 ? "border-top" : ""}`}
              style={{ borderColor: "var(--bs-border-color-translucent, #dee2e6)" }}
            >
              <div
                className="text-truncate fw-medium flex-shrink-0"
                style={{ width: "min(11rem, 28vw)", minWidth: 0 }}
                title={p.name || undefined}
              >
                {p.name || "Untitled product"}
              </div>
              {!p.enabled && (
                <span className="badge bg-secondary align-self-center" style={{ fontSize: "0.65rem" }}>
                  Disabled
                </span>
              )}
              <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1 justify-content-start">
                {p.images.length === 0 ? (
                  <span className="text-muted small">No images</span>
                ) : (
                  p.images.map((img) => {
                    const busy = busyKey === `${p.id}_${img.id}`;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        className="btn p-0 rounded-2 position-relative overflow-visible flex-shrink-0"
                        style={{
                          width: THUMB_PX,
                          height: THUMB_PX,
                          background: "#f1f3f5",
                          padding: 0,
                          border: img.isMain ? "3px solid #0d6efd" : "1px solid #adb5bd",
                          boxShadow: img.isMain
                            ? "0 0 0 2px #ffffff, 0 0 0 5px #0d6efd, 0 2px 8px rgba(13, 110, 253, 0.45)"
                            : "none",
                          zIndex: img.isMain ? 1 : 0,
                        }}
                        disabled={!!busyKey}
                        title={img.isMain ? "Current primary image" : "Set as primary image"}
                        onClick={() => {
                          if (img.isMain) return;
                          handleSetMain(p.id, img.id);
                        }}
                      >
                        <span
                          className="d-block w-100 h-100 overflow-hidden rounded-1"
                          style={{ lineHeight: 0 }}
                        >
                          {img.thumbUrl ? (
                            <img
                              src={img.thumbUrl}
                              alt=""
                              className="w-100 h-100"
                              style={{ objectFit: "cover" }}
                            />
                          ) : (
                            <span className="small text-muted">—</span>
                          )}
                        </span>
                        {busy && (
                          <span className="position-absolute bottom-0 start-50 translate-middle-x mb-0" style={{ zIndex: 2 }}>
                            <span className="spinner-border spinner-border-sm text-primary" style={{ width: "1rem", height: "1rem" }} role="status" />
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && products.length === 0 && (
        <p className="text-muted text-center py-3 mb-0 small">No products match your filters.</p>
      )}

      {!loading && total > 0 && (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 pt-2 border-top small text-muted">
          <span>
            Showing {rangeStart}–{rangeEnd} of {total}
            {hasMultiplePages ? ` · Page ${page} of ${totalPages}` : ""}
          </span>
          {hasMultiplePages && (
            <div className="btn-group btn-group-sm">
              <button
                type="button"
                className="btn btn-outline-secondary py-0"
                disabled={page <= 1 || loading}
                onClick={() => setPage((x) => Math.max(1, x - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary py-0"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((x) => x + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
